import { z } from "zod";
import {
  buildLayoutContainerSchema,
  collectLayoutLeaves,
  collectLayoutNodeIds,
  genLayoutId,
  getLayoutDepth,
  isLayoutContainer,
  type LayoutContainer,
} from "../lib/layoutTree";

/**
 * Le board : une frame dont l'arbre de layout (cf. `convex/lib/layoutTree.ts`)
 * a pour feuilles des ESPACES, qui rangent d'autres nodes du canvas.
 *
 * Un enfant du board le déclare en `parentId`, comme pour une frame, et porte
 * en plus un `placement` (espace + clé d'ordre) sur sa ligne `nodes`. C'est
 * la seule vraie donnée : sa position s'en déduit, mesurée par le canvas
 * (cf. `useBoardLayout`).
 *
 * L'arbre est propre à chaque board : un board neuf part de trois colonnes
 * (`defaultBoardLayout`), qu'on remodèle dans l'éditeur.
 */

/**
 * Un espace. Son nom ne se lit que dans l'éditeur de mise en page (et par
 * Nolë) : sur le board, ce sont des textes ou des title nodes qui titrent.
 */
export type BoardSpace = { kind: "space"; id: string; name?: string };
export type BoardDivider = { kind: "divider"; id: string };

/** L'échelle des titres du canvas (cf. `titleLevelStyles`), plus le texte courant. */
export const BOARD_TEXT_LEVELS = ["h1", "h2", "h3", "p"] as const;
export type BoardTextLevel = (typeof BOARD_TEXT_LEVELS)[number];

/** Un texte fixe de la mise en page : un en-tête de colonne, un intitulé d'axe. */
export type BoardText = {
  kind: "text";
  id: string;
  content: string;
  level: BoardTextLevel;
};

export type BoardLeaf = BoardSpace | BoardDivider | BoardText;
export type BoardLayout = LayoutContainer<BoardLeaf>;

const leafId = z.string().min(1).max(64);

const boardLeafSchema: z.ZodType<BoardLeaf> = z.union([
  z.strictObject({
    kind: z.literal("space"),
    id: leafId,
    name: z.string().max(60).optional(),
  }),
  z.strictObject({ kind: z.literal("divider"), id: leafId }),
  z.strictObject({
    kind: z.literal("text"),
    id: leafId,
    content: z.string().max(200),
    level: z.enum(BOARD_TEXT_LEVELS),
  }),
]);

export const boardLayoutSchema = buildLayoutContainerSchema(boardLeafSchema);

/** Au-delà, l'arbre ne se lit plus d'un coup d'œil. */
export const MAX_BOARD_LAYOUT_DEPTH = 4;

/** Marges codées en dur pour commencer (cf. spec) : une seule valeur partout. */
export const BOARD_GAP = 16;

/**
 * Un espace vide garde une taille où déposer, et une colonne de Kanban une
 * largeur lisible même avec des cartes étroites.
 */
export const SPACE_MIN_WIDTH = 280;
export const SPACE_MIN_HEIGHT = 120;

const space = (): BoardSpace => ({ kind: "space", id: genLayoutId("s") });

const container = (
  direction: "row" | "column",
  children: BoardLayout["children"],
): BoardLayout => ({
  kind: "container",
  id: genLayoutId("c"),
  direction,
  gap: BOARD_GAP,
  align: "stretch",
  children,
});

/** Bornes d'une grille construite d'un coup (cf. `buildBoardGrid`). */
export const MAX_BOARD_COLUMNS = 12;
export const MAX_BOARD_ROWS = 8;

/**
 * Une grille de `columns` × `rows` espaces : une ligne d'espaces pour une
 * seule rangée (un Kanban), une colonne de lignes sinon (une matrice). Les
 * espaces se lisent ligne par ligne.
 */
export function buildBoardGrid(columns: number, rows: number): BoardLayout {
  const row = () =>
    container(
      "row",
      Array.from({ length: columns }, () => space()),
    );
  const root =
    rows <= 1
      ? row()
      : container(
          "column",
          Array.from({ length: rows }, () => row()),
        );
  return { ...root, padding: BOARD_GAP };
}

/** La taille d'une grille vide, pour qu'un board neuf ne naisse pas à zéro. */
export function boardGridSize(columns: number, rows: number) {
  return {
    width: columns * (SPACE_MIN_WIDTH + BOARD_GAP) + BOARD_GAP,
    height: rows * (SPACE_MIN_HEIGHT + BOARD_GAP) + BOARD_GAP,
  };
}

/** L'arbre d'un board neuf : trois colonnes, à remodeler dans l'éditeur. */
export const defaultBoardLayout = () => buildBoardGrid(3, 1);

/**
 * L'arbre tel que le board le rend : chaque container sous la racine grandit
 * pour remplir la place. C'est ce qui étire les espaces quand on agrandit le
 * board — un Kanban élargi garde des colonnes égales, une matrice des lignes
 * égales. Rendu seulement : l'arbre stocké n'en porte rien.
 */
export function fillingTree(layout: BoardLayout): BoardLayout {
  const fill = (node: BoardLayout["children"][number]): typeof node =>
    isLayoutContainer(node)
      ? { ...node, grow: node.grow ?? 1, children: node.children.map(fill) }
      : node;
  return { ...layout, children: layout.children.map(fill) };
}

/** Entre un en-tête et son espace : plus serré qu'entre deux espaces. */
const SPACE_HEADER_GAP = 8;

/**
 * Un en-tête de texte au-dessus des premiers espaces, dans l'ordre de
 * lecture : chacun devient une colonne [texte, espace], et l'espace prend
 * l'en-tête pour nom. Une chaîne vide laisse son espace tel quel.
 */
export function withSpaceHeaders(
  layout: BoardLayout,
  headers: string[],
): BoardLayout {
  let index = 0;
  const walk = (node: BoardLayout["children"][number]): typeof node => {
    if (node.kind === "container") {
      return { ...node, children: node.children.map(walk) };
    }
    if (node.kind !== "space") return node;
    const header = headers[index++]?.trim();
    if (!header) return node;
    return {
      ...container("column", [
        {
          kind: "text",
          id: genLayoutId("t"),
          content: header.slice(0, 200),
          level: "h3",
        },
        { ...node, name: header.slice(0, 60) },
      ]),
      gap: SPACE_HEADER_GAP,
    };
  };
  return walk(layout) as BoardLayout;
}

/**
 * Un arbre proposé par l'agent, complété puis validé — ou refusé avec la
 * raison, à lui renvoyer.
 *
 * Complété : un élément sans `id` en reçoit un (un espace sans id est un
 * espace NEUF : ceux à garder, avec leurs nodes, se désignent par le leur),
 * et une boîte sans réglages prend ceux du board. Refusé : forme invalide,
 * id en double, profondeur dépassée, aucun espace.
 */
export function parseAgentBoardLayout(
  raw: unknown,
): { layout: BoardLayout } | { error: string } {
  const complete = (node: unknown, isRoot: boolean): unknown => {
    if (!node || typeof node !== "object") return node;
    const item = node as Record<string, unknown>;
    const kind = typeof item.kind === "string" ? item.kind : "";
    const id = item.id ?? genLayoutId(kind.charAt(0) || "n");
    if (kind !== "container") return { ...item, id };
    return {
      gap: BOARD_GAP,
      align: "stretch",
      ...(isRoot && { padding: BOARD_GAP }),
      ...item,
      id,
      children: Array.isArray(item.children)
        ? item.children.map((child) => complete(child, false))
        : item.children,
    };
  };

  const parsed = boardLayoutSchema.safeParse(complete(raw, true));
  if (!parsed.success) {
    const issues = parsed.error.issues
      .slice(0, 3)
      .map((issue) => `${issue.path.join(".") || "layout"}: ${issue.message}`);
    return { error: `Invalid layout. ${issues.join("; ")}` };
  }
  const layout = parsed.data;
  const ids = collectLayoutNodeIds(layout);
  const duplicate = ids.find((id, i) => ids.indexOf(id) !== i);
  if (duplicate)
    return { error: `Invalid layout: id "${duplicate}" is used twice.` };
  if (getLayoutDepth(layout) > MAX_BOARD_LAYOUT_DEPTH) {
    return {
      error: `Invalid layout: at most ${MAX_BOARD_LAYOUT_DEPTH} levels of rows and columns.`,
    };
  }
  if (listBoardSpaces(layout).length === 0) {
    return { error: "Invalid layout: a board needs at least one space." };
  }
  return { layout };
}

/** L'arbre stocké, ou `null` s'il n'a pas la forme (rendu dégradé, pas de crash). */
export function readBoardLayout(value: unknown): BoardLayout | null {
  const parsed = boardLayoutSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function listBoardSpaces(layout: BoardLayout): BoardSpace[] {
  return collectLayoutLeaves(layout).filter(
    (leaf): leaf is BoardSpace => leaf.kind === "space",
  );
}

/**
 * L'espace d'un enfant : le sien s'il existe encore, sinon le premier. Un
 * espace retiré de l'arbre ne fait donc disparaître aucun node — il retombe
 * dans le premier, sans écriture.
 */
export function resolveSpaceId(
  layout: BoardLayout,
  spaceId: string | undefined,
): string | null {
  const spaces = listBoardSpaces(layout);
  if (spaceId && spaces.some((s) => s.id === spaceId)) return spaceId;
  return spaces[0]?.id ?? null;
}

/**
 * Un arbre neuf (un autre modèle) qui reprend les espaces de l'ancien, dans
 * l'ordre, avec leur nom : passer d'un Kanban à une matrice laisse les nodes
 * de la première colonne dans la première case, et ainsi de suite. Les
 * espaces en trop de l'ancien arbre disparaissent — leurs nodes retombent
 * dans le premier.
 */
export function reuseSpaceIds(
  next: BoardLayout,
  previous: BoardLayout,
): BoardLayout {
  const spaces = listBoardSpaces(previous);
  let index = 0;
  const walk = (node: BoardLayout["children"][number]): typeof node => {
    if (node.kind === "container") {
      return { ...node, children: node.children.map(walk) };
    }
    if (node.kind === "space" && index < spaces.length) {
      return spaces[index++];
    }
    return node;
  };
  return walk(next) as BoardLayout;
}

/** Un enfant de board, vu par son rangement. */
type PlacedChild = {
  id: string;
  placement?: { spaceId: string; order: string } | null;
};

/**
 * Les enfants d'un board, espace par espace, dans l'ordre de leur clé : la
 * même lecture pour le canvas qui les pose et pour Nolë qui les lit.
 *
 * Un enfant sans clé (rangé par un autre chemin qu'un drag) va en fin
 * d'espace ; à clés égales, l'id départage. Un espace disparu renvoie au
 * premier (cf. `resolveSpaceId`). Chaque espace de l'arbre a son entrée,
 * vide comprise.
 */
export function groupBoardChildren<Child extends PlacedChild>(
  layout: BoardLayout,
  children: Child[],
): Map<string, Child[]> {
  const bySpace = new Map<string, Child[]>(
    listBoardSpaces(layout).map((space) => [space.id, []]),
  );
  for (const child of children) {
    const spaceId = resolveSpaceId(layout, child.placement?.spaceId);
    if (spaceId) bySpace.get(spaceId)!.push(child);
  }
  const orderOf = (child: Child) => child.placement?.order ?? "~";
  for (const group of bySpace.values()) {
    group.sort((a, b) =>
      orderOf(a) === orderOf(b)
        ? a.id.localeCompare(b.id)
        : orderOf(a) < orderOf(b)
          ? -1
          : 1,
    );
  }
  return bySpace;
}

/**
 * Une géométrie de principe pour les enfants d'un board, là où le serveur
 * doit raisonner en positions (le plan du canvas rattache un node au titre
 * au-dessus de lui ; `nole read` donne des coordonnées) : leur vraie place,
 * le canvas la calcule sans l'écrire (cf. `useBoardLayout`).
 *
 * Un espace par colonne, dans l'ordre de lecture, ses nodes empilés dans leur
 * ordre. Relative au board, comme toute position d'enfant. Fausse au pixel
 * près, juste dans ce qui compte : qui est dans quel espace, et dans quel
 * ordre.
 */
export function schematicBoardPositions<
  Child extends PlacedChild & { height?: number | null },
>(
  layout: BoardLayout,
  children: Child[],
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  [...groupBoardChildren(layout, children).values()].forEach((group, i) => {
    let y = BOARD_GAP;
    for (const child of group) {
      positions.set(child.id, {
        x: BOARD_GAP + i * (SPACE_MIN_WIDTH + BOARD_GAP),
        y,
      });
      y += (child.height ?? 0) + BOARD_GAP;
    }
  });
  return positions;
}
